import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { sanityClient, urlFor } from "../lib/sanityClient";
import { ease, spring } from "../lib/motion";
import "../styles/TitleBar.css";

const QUERY = `*[_type == "siteSettings"][0]{
  logoDarkDesktop, logoDarkMobile, logoLightDesktop, logoLightMobile,
  arrowIcon, hamburgerIcon, ctaText, ctaUrl, navItems
}`;

const TitleBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isDesktop, setIsDesktop] = useState(true);
  const [data, setData] = useState(null);
  const [hoveredNav, setHoveredNav] = useState(null);

  useEffect(() => {
    sanityClient.fetch(QUERY).then(setData);
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const titlebar = document.querySelector(".titlebar");
      if (!titlebar) return;
      
      const sections = document.querySelectorAll("[data-theme]");
      const navbarHeight = titlebar.offsetHeight || 0;
      
      let current = "";
      for (let section of sections) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= navbarHeight + 50 && rect.bottom > navbarHeight + 50) {
          const id = section.getAttribute("id");
          current = id;

          const theme = section.getAttribute("data-theme");
          if (theme === "light") {
            titlebar.classList.add("light-theme");
          } else {
            titlebar.classList.remove("light-theme");
          }
          break;
        }
      }
      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };

    handleScroll();
    handleResize();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (!data) return null;

  const navItems = data.navItems || [];

  const logoDark = isDesktop
    ? urlFor(data.logoDarkDesktop).url()
    : urlFor(data.logoDarkMobile).url();

  const logoLight = isDesktop
    ? urlFor(data.logoLightDesktop).url()
    : urlFor(data.logoLightMobile).url();

  return (
    <header className="titlebar">
      <motion.div
        className="titlebar-content"
        initial={{ opacity: 0, y: -24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease }}
      >
        <div className="logo-nav-group">
          <div
            className="logo-container"
            onClick={() => window.location.reload()}
            style={{ cursor: "pointer" }}
          >
            <img src={logoDark} alt="Logo Dark" className="logo logo-dark" />
            <img src={logoLight} alt="Logo Light" className="logo logo-light" />
          </div>

          <motion.nav
            className={`nav-links ${menuOpen ? "active" : ""}`}
            initial={false}
            animate={{ opacity: !isDesktop && !menuOpen ? 0 : 1 }}
            transition={{ duration: 0.3, ease }}
          >
            {navItems.map(({ label, href, _key }, i) => {
              const isActive = activeSection === href.substring(1);
              const showLine = isActive || hoveredNav === _key;
              return (
                <motion.a
                  key={_key}
                  href={href}
                  className="nav-item"
                  initial={{ opacity: 0, y: -12 }}
                  animate={
                    !isDesktop && !menuOpen
                      ? { opacity: 0, y: -12 }
                      : { opacity: 1, y: 0 }
                  }
                  transition={{
                    duration: 0.6,
                    ease,
                    delay: menuOpen ? 0.1 + i * 0.08 : 0.3 + i * 0.07,
                  }}
                  onHoverStart={() => setHoveredNav(_key)}
                  onHoverEnd={() => setHoveredNav(null)}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.preventDefault();
                    const target = document.querySelector(href);
                    if (target) {
                      target.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                      setMenuOpen(false);
                    }
                  }}
                >
                  {label}
                  <motion.span
                    className="nav-underline"
                    initial={false}
                    animate={{ scaleX: showLine ? 1 : 0 }}
                    transition={{ duration: 0.35, ease }}
                  />
                </motion.a>
              );
            })}
          </motion.nav>
        </div>

        <motion.button
          className="cta-button"
          onClick={() => window.open(data.ctaUrl, "_blank")}
          initial="rest"
          animate="rest"
          whileHover="hover"
          whileTap={{ scale: 0.96 }}
          variants={{ rest: { scale: 1 }, hover: { scale: 1.05 } }}
          transition={spring}
        >
          <span className="cta-text">{data.ctaText}</span>
          <motion.span
            className="arrow-icon"
            variants={{ rest: { rotate: 0 }, hover: { rotate: 45 } }}
            transition={{ duration: 0.35, ease }}
          >
            <img src={urlFor(data.arrowIcon).url()} alt="arrow icon" />
          </motion.span>
        </motion.button>

        <motion.button
          className="hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          whileTap={{ scale: 0.88 }}
        >
          <img
            src={urlFor(data.hamburgerIcon).url()}
            alt="Menu"
            className="hamburger-icon"
          />
        </motion.button>

        {menuOpen && (
          <motion.button
            className="close-button"
            onClick={() => setMenuOpen(false)}
            initial={{ opacity: 0, rotate: -90 }}
            animate={{ opacity: 1, rotate: 0 }}
            transition={{ duration: 0.4, ease }}
            whileTap={{ scale: 0.88 }}
          >
            ✕
          </motion.button>
        )}
      </motion.div>
    </header>
  );
};

export default TitleBar;
